import { useEffect, useRef } from "react";
import { useSetRecoilState, useRecoilValue } from "recoil";
import {
  zoomNotificationState,
  zoomNotificationLoadingState,
  zoomNotificationErrorState,
  zoomNotificationInitializedState,
} from "../../../../../recoil/home/tabs/dashboard/components/zoomNotification";
import {
  getZoomNotifications,
  subscribeToZoomNotifications,
} from "../../../../../services/firestore";
import { validateZoomNotificationArray } from "../../../../../models/zoomNotification";

// Zoom Notification 초기화 훅
export const useInitializeZoomNotification = () => {
  const setZoomNotifications = useSetRecoilState(zoomNotificationState);
  const setLoading = useSetRecoilState(zoomNotificationLoadingState);
  const setError = useSetRecoilState(zoomNotificationErrorState);
  const setInitialized = useSetRecoilState(zoomNotificationInitializedState);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;

    const initializeNotifications = async () => {
      hasInitialized.current = true;
      setLoading(true);
      setError(null);

      try {
        const notifications = await getZoomNotifications();

        if (validateZoomNotificationArray(notifications)) {
          setZoomNotifications(notifications);
        } else {
          // 데이터가 없거나 형식이 맞지 않을 때만 경고 (정상적인 경우일 수 있음)
          if (notifications && notifications.length > 0) {
            console.warn("❌ Invalid zoom notifications data format:", notifications);
          }
          setZoomNotifications([]);
        }

        setInitialized(true);
      } catch (error) {
        console.error("Failed to initialize zoom notifications:", error);
        setError(error.message);
        setZoomNotifications([]);
        setInitialized(true);
      } finally {
        setLoading(false);
      }
    };

    initializeNotifications();
  }, []); // 빈 의존성 배열로 변경
};

// Zoom Notification 실시간 구독 훅
export const useSubscribeToZoomNotification = () => {
  const setZoomNotifications = useSetRecoilState(zoomNotificationState);
  const setError = useSetRecoilState(zoomNotificationErrorState);

  useEffect(() => {
    const unsubscribe = subscribeToZoomNotifications((notifications) => {
      if (validateZoomNotificationArray(notifications)) {
        setZoomNotifications(notifications);
      } else {
        // 데이터가 없거나 형식이 맞지 않을 때만 경고 (정상적인 경우일 수 있음)
        if (notifications && notifications.length > 0) {
          console.warn(
            "Invalid zoom notifications data received from real-time listener:",
            notifications
          );
        }
        setZoomNotifications([]);
      }
    });

    return () => unsubscribe();
  }, [setZoomNotifications, setError]);
};

// Zoom Notification 사용 훅
export const useZoomNotification = () => {
  const notifications = useRecoilValue(zoomNotificationState);
  const loading = useRecoilValue(zoomNotificationLoadingState);
  const error = useRecoilValue(zoomNotificationErrorState);
  const initialized = useRecoilValue(zoomNotificationInitializedState);

  return {
    notifications,
    loading,
    error,
    initialized,
    isReady: initialized && !loading && !error,
  };
};
